// BigQueryEditor.tsx
import { useRef, useState, useEffect, useCallback } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { Button, Box } from "@mui/material";

// Load Google Fonts for better typography
const loadFonts = () => {
  const link = document.createElement("link");
  link.href =
    "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Fira+Code:wght@400;500&display=swap";
  link.rel = "stylesheet";
  if (!document.querySelector(`link[href="${link.href}"]`)) {
    document.head.appendChild(link);
  }
};

interface BigQueryEditorProps {
  value?: string;
  minHeight?: number;
  maxHeight?: number;
  theme?: string;
  onChange?: (value: string) => void;
  showFormatButton?: boolean;
  readOnly?: boolean;
}

export default function BigQueryEditor({
  value = "",
  minHeight = 300,
  maxHeight = 800,
  theme = "vs-light",
  onChange,
  showFormatButton = true,
  readOnly = false,
}: BigQueryEditorProps) {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFormatting, setIsFormatting] = useState(false);
  const [formatError, setFormatError] = useState<string>("");
  const [sqlFormatterAvailable, setSqlFormatterAvailable] = useState(false);
  const [height, setHeight] = useState(minHeight);
  const [isResizing, setIsResizing] = useState(false);

  // Load fonts when component mounts
  useEffect(() => {
    loadFonts();
  }, []);

  // Check if sql-formatter is available
  useEffect(() => {
    const checkSqlFormatter = async () => {
      try {
        // Check if sql-formatter is available globally
        if (typeof window !== "undefined" && (window as any).sqlFormatter) {
          setSqlFormatterAvailable(true);
          return;
        }

        // Try to dynamically import it
        const sqlFormatterModule = (await import("sql-formatter")) as any;
        if (sqlFormatterModule && sqlFormatterModule.format) {
          (window as any).sqlFormatter = sqlFormatterModule;
          setSqlFormatterAvailable(true);
        }
      } catch (error) {
        console.warn("SQL Formatter not available, using basic formatting");
        setSqlFormatterAvailable(false);
      }
    };

    checkSqlFormatter();
  }, []);

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Add keyboard shortcut for formatting (Ctrl+Shift+F / Cmd+Shift+F)
    if (!readOnly) {
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
        () => {
          formatCode();
        }
      );
    }
    if (value.trim()) {
      // Use setTimeout to ensure editor is fully initialized
      setTimeout(() => {
        formatCode();
      }, 100);
    }
  };

  // BigQuery-specific SQL formatter function
  const formatBigQueryCode = async (code: string): Promise<string | null> => {
    if (!code.trim()) return null;

    try {
      if (sqlFormatterAvailable) {
        const sqlFormatter = (window as any).sqlFormatter;
        return sqlFormatter.format(code, {
          language: "bigquery", // BigQuery specific
          uppercase: true,
          linesBetweenQueries: 2,
          keywordCase: "upper",
          functionCase: "upper",
          dataTypeCase: "upper",
          indentStyle: "standard",
        });
      } else {
        // Fallback to BigQuery-specific basic formatting
        return basicBigQueryFormat(code);
      }
    } catch (error) {
      console.error("BigQuery formatting error:", error);
      // Final fallback to basic formatting
      return basicBigQueryFormat(code);
    }
  };

  // Basic BigQuery formatting fallback with BigQuery-specific keywords
  const basicBigQueryFormat = (sql: string): string => {
    const bigQueryKeywords = [
      "SELECT",
      "FROM",
      "WHERE",
      "JOIN",
      "INNER JOIN",
      "LEFT JOIN",
      "RIGHT JOIN",
      "FULL OUTER JOIN",
      "GROUP BY",
      "ORDER BY",
      "HAVING",
      "INSERT INTO",
      "UPDATE",
      "DELETE FROM",
      "CREATE TABLE",
      "CREATE OR REPLACE TABLE",
      "ALTER TABLE",
      "DROP TABLE",
      "WITH",
      "UNION ALL",
      "UNION",
      "EXCEPT",
      "INTERSECT",
      "WINDOW",
      "PARTITION BY",
      "CLUSTER BY",
      "CREATE VIEW",
      "CREATE OR REPLACE VIEW",
      "CREATE MATERIALIZED VIEW",
      "CREATE FUNCTION",
      "CREATE OR REPLACE FUNCTION",
      "DECLARE",
      "SET",
      "CALL",
      "EXPORT DATA",
      "LOAD DATA",
      "MERGE",
    ];

    const keywordPattern = new RegExp(
      `\\b(${bigQueryKeywords.join("|")})\\b`,
      "gi"
    );

    return sql
      .replace(/\s+/g, " ") // Collapse multiple spaces
      .replace(/\s*([(),;=<>])\s*/g, " $1 ") // Space around operators
      .replace(keywordPattern, "\n$1 ") // New lines for BigQuery keywords
      .replace(/;\s*/g, ";\n\n") // New lines after semicolons
      .replace(/,\s*/g, ",\n  ") // Format comma-separated items
      .trim();
  };

  // Format button handler
  const formatCode = async () => {
    if (!editorRef.current) return;

    setIsFormatting(true);
    setFormatError("");

    try {
      const currentValue = editorRef.current.getValue();
      const formatted = await formatBigQueryCode(currentValue);

      if (formatted && formatted !== currentValue) {
        editorRef.current.setValue(formatted);
        onChange?.(formatted);
      }
    } catch (error) {
      setFormatError("Failed to format BigQuery code");
      console.error("Format error:", error);
    } finally {
      setIsFormatting(false);
    }
  };

  const handleChange = (val: string | undefined) => {
    if (readOnly) return;
    const newValue = val || "";
    onChange?.(newValue);
  };

  // Resize functionality
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startY = e.clientY;
      const startHeight = height;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaY = e.clientY - startY;
        const newHeight = Math.min(
          Math.max(startHeight + deltaY, minHeight),
          maxHeight
        );
        setHeight(newHeight);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [height, minHeight, maxHeight]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
        userSelect: isResizing ? "none" : "auto",
        "& .monaco-editor": {
          borderRadius: "0 0 8px 8px",
        },
        "& .monaco-editor .margin": {
          backgroundColor: "#fafafa !important",
        },
        "& .monaco-editor .monaco-editor-background": {
          backgroundColor: "#ffffff !important",
        },
        "& .monaco-editor .current-line": {
          backgroundColor: "#f5f5f5 !important",
        },
        "& .monaco-editor .line-numbers": {
          color: "#999999 !important",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: "500",
            color: "#333333",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Requested SQL
        </Box>
        {readOnly && (
          <Box
            sx={{
              fontSize: "11px",
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontWeight: "500",
              color: "#666666",
              backgroundColor: "#e9ecef",
              padding: "4px 8px",
              borderRadius: "4px",
              border: "1px solid #ced4da",
            }}
          >
            READ-ONLY
          </Box>
        )}
      </Box>

      {/* Editor Container */}
      <Box sx={{ position: "relative" }}>
        <Editor
          height={`${height}px`}
          defaultLanguage="sql"
          defaultValue={value}
          theme={theme}
          onMount={handleEditorDidMount}
          onChange={readOnly ? undefined : handleChange}
          options={{
            readOnly,
            lineNumbersMinChars: 5,
            minimap: { enabled: false },
            fontSize: 12,
            fontFamily:
              "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', 'Menlo', 'DejaVu Sans Mono', 'Liberation Mono', 'Consolas', 'Ubuntu Mono', monospace",
            fontLigatures: true,
            fontWeight: "400",
            lineHeight: 1.6,
            letterSpacing: 0.5,
            lineNumbers: "on",
            roundedSelection: false,
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            wordWrap: "on",
            contextmenu: !readOnly,
            selectOnLineNumbers: true,
            bracketPairColorization: { enabled: true },
            folding: true,
            foldingHighlight: true,
            showFoldingControls: "mouseover",
            matchBrackets: "always",
            autoIndent: "full",
            formatOnPaste: !readOnly,
            formatOnType: !readOnly,
            suggestOnTriggerCharacters: !readOnly,
            acceptSuggestionOnEnter: readOnly ? "off" : "on",
            hover: { enabled: true },
            parameterHints: { enabled: !readOnly },
            quickSuggestions: readOnly
              ? false
              : {
                  other: true,
                  comments: false,
                  strings: false,
                },
            snippetSuggestions: readOnly ? "none" : "inline",
            wordBasedSuggestions: readOnly ? "off" : "allDocuments",
            cursorBlinking: "smooth",
            cursorSmoothCaretAnimation: "on",
            smoothScrolling: true,
            padding: { top: 16, bottom: 16 },
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 14,
              horizontalScrollbarSize: 14,
            },
          }}
        />
      </Box>

      {/* Resize Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: "absolute",
          bottom: "2px",
          right: "2px",
          width: "16px",
          height: "16px",
          cursor: "se-resize",
          zIndex: 1001,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "2px",
            right: "2px",
            width: "12px",
            height: "12px",
            background: `
              linear-gradient(135deg, transparent 30%, #cccccc 30%, #cccccc 40%, transparent 40%),
              linear-gradient(45deg, transparent 30%, #cccccc 30%, #cccccc 40%, transparent 40%)
            `,
            backgroundSize: "4px 4px",
            opacity: 0.6,
            transition: "opacity 0.2s ease",
          },
          "&:hover::after": {
            opacity: 0.8,
          },
        }}
      />

      {showFormatButton && (
        <Box
          sx={{
            position: "absolute",
            bottom: "12px",
            right: "24px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            zIndex: 1000,
          }}
        >
          {formatError && (
            <Box
              sx={{
                color: "#dc3545",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: "#f8d7da",
                padding: "6px 10px",
                borderRadius: "4px",
                border: "1px solid #f5c6cb",
              }}
            >
              {formatError}
            </Box>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={formatCode}
            disabled={isFormatting}
            size="small"
            sx={{
              borderRadius: "4px",
              padding: "6px 12px",
              fontSize: "12px",
              fontWeight: "500",
              textTransform: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              minWidth: "auto",
              "&:hover": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              },
              "&:active": {
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              },
            }}
            title="Format SQL (Ctrl+Shift+F)"
          >
            {isFormatting ? "Formatting..." : "Format"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
